import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtNum, fmtDate } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { ActionCard, CategoryBadge, TagChips, PriorityBadge } from "@finance/components/ActionBits";
import { CATEGORIES, sortActions } from "@finance/lib/actionColors";
import { Button } from "@finance/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { DownloadSimple, Trash, Rows, SquaresFour, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { tagClass } from "@finance/lib/actionColors";

export default function AllActions() {
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ assigned_to: "all", status: "all", category: "all" });
  const [sort, setSort] = useState("assigned");
  const [view, setView] = useState("table");
  const [tagFilter, setTagFilter] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (f.assigned_to !== "all") params.assigned_to = f.assigned_to;
    if (f.status !== "all") params.status = f.status;
    if (f.category !== "all") params.category = f.category;
    api.get("/actions/all", { params }).then((r) => setActions(r.data));
  }, [f]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this action? It will move to Deleted Items and can be restored.")) return;
    await api.delete(`/actions/${id}`);
    toast.success("Moved to Deleted Items");
    load();
  };

  const cols = [
    { key: "date_assigned", label: "Date", format: fmtDate },
    { key: "due_date", label: "Due", format: fmtDate },
    { key: "category", label: "Category" },
    { key: "priority", label: "Priority" },
    { key: "assigned_to_name", label: "Assignee" },
    { key: "details", label: "Details" },
    { key: "tags", label: "Tags", format: (v) => (v || []).join(" ") },
    { key: "amount", label: "Amount", format: (v) => (v != null ? fmtNum(v) : "") },
    { key: "status", label: "Status" },
    { key: "created_by_name", label: "Created By" },
  ];

  const base = tagFilter ? actions.filter((a) => (a.tags || []).some((t) => t.toLowerCase() === tagFilter.toLowerCase())) : actions;
  const rows = sortActions(base, sort);

  return (
    <Layout>
      <PageHeader
        title="All Actions"
        subtitle="Shared public action items"
        actions={
          <Button variant="outline" data-testid="export-actions" onClick={() => exportToCsv("all-actions", rows, cols)}>
            <DownloadSimple size={16} className="mr-1" /> Export
          </Button>
        }
      />
      <div className="p-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <Select value={f.assigned_to} onValueChange={(v) => setF({ ...f, assigned_to: v })}>
              <SelectTrigger className="h-9 w-44" data-testid="filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All assignees</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger className="h-9 w-44" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="h-9 w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-40" data-testid="sort-actions"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assigned">Sort: Newest</SelectItem>
                <SelectItem value="due">Sort: Due date</SelectItem>
                <SelectItem value="priority">Sort: Priority</SelectItem>
                <SelectItem value="category">Sort: Category</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="inline-flex rounded-sm border border-gray-200 bg-white p-0.5" data-testid="actions-view-switch">
            {[{ k: "table", icon: Rows, label: "Table" }, { k: "grid", icon: SquaresFour, label: "Grid" }].map((o) => (
              <button key={o.k} data-testid={`view-${o.k}`} onClick={() => setView(o.k)} className={`inline-flex items-center gap-1.5 rounded-[3px] px-2.5 py-1 text-xs font-semibold transition-colors ${view === o.k ? "bg-[#1B4383] text-white" : "text-gray-500 hover:bg-gray-100"}`}>
                <o.icon size={15} weight={view === o.k ? "fill" : "regular"} />{o.label}
              </button>
            ))}
          </div>
        </div>

        {tagFilter && (
          <div className="mb-4 flex items-center gap-2 text-sm" data-testid="tag-filter-banner">
            <span className="text-gray-500">Filtered by tag:</span>
            <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium ${tagClass(tagFilter)}`}>#{tagFilter}</span>
            <button data-testid="clear-tag-filter" onClick={() => setTagFilter(null)} className="inline-flex items-center gap-0.5 text-xs text-gray-400 hover:text-red-500"><X size={12} />clear</button>
          </div>
        )}

        {view === "grid" ? (
          rows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400">No public actions.</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="actions-grid">
              {rows.map((a) => <ActionCard key={a.id} a={a} onDelete={(x) => del(x.id)} onTagClick={setTagFilter} showCreatedBy />)}
            </div>
          )
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="lx-label border-b border-gray-200">
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Due</th>
                  <th className="px-4 py-2 text-left">Category</th>
                  <th className="px-4 py-2 text-left">Priority</th>
                  <th className="px-4 py-2 text-left">Assignee</th>
                  <th className="px-4 py-2 text-left">Details</th>
                  <th className="px-4 py-2 text-left">Tags</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Created By</th>
                  <th className="px-4 py-2 text-right">·</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <TableEmpty colSpan={11} title="No public actions" message="Shared team actions will appear here." />}
                {rows.map((a) => {
                  const overdue = a.status === "Pending" && a.due_date && a.due_date < new Date().toISOString().slice(0, 10);
                  return (
                    <tr key={a.id} data-testid={`all-action-row-${a.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2 lx-num">{fmtDate(a.date_assigned)}</td>
                      <td className={`px-4 py-2 lx-num ${overdue ? "font-semibold text-red-600" : ""}`}>{a.due_date ? fmtDate(a.due_date) : "—"}</td>
                      <td className="px-4 py-2"><CategoryBadge category={a.category} /></td>
                      <td className="px-4 py-2"><PriorityBadge priority={a.priority} /></td>
                      <td className="px-4 py-2">{a.assigned_to_name}</td>
                      <td className="px-4 py-2 max-w-xs">{a.details}</td>
                      <td className="px-4 py-2"><div className="flex flex-wrap gap-1"><TagChips tags={a.tags} onTagClick={setTagFilter} /></div></td>
                      <td className="px-4 py-2 text-right lx-num">{a.amount != null ? fmtNum(a.amount) : "—"}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded-sm px-1.5 py-0.5 text-xs ${a.status === "Completed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{a.status}</span>
                      </td>
                      <td className="px-4 py-2 text-gray-500">{a.created_by_name}</td>
                      <td className="px-4 py-2 text-right">
                        <button className="text-gray-400 hover:text-red-500" data-testid={`all-action-delete-${a.id}`} onClick={() => del(a.id)}><Trash size={15} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
